import { useState, type JSX, type KeyboardEvent } from "react";
import { LogIn } from "lucide-react";
import { ApiFehler, VerbindungsFehler } from "../../api/basis.ts";
import { FEHLERTEXTE, NutzerFehler, fehlertext } from "../../lib/fehlertext.ts";
import type { Nutzer } from "../../state/reduzierer.ts";

/**
 * Antwort von `POST /api/auth/login`. `user` ist seit dem Merge der Tasks 7-9 der `Nutzer`-Typ des
 * Stores (`state/reduzierer.ts`) statt einer eigenen, um `active` verkuerzten Kopie: `App.tsx`
 * reicht dieses Objekt unveraendert als `{ art: "angemeldet", nutzer }` in den Store weiter, und mit
 * zwei getrennten Typen fuer denselben Datensatz muesste es dort ein Feld erfinden, das die Antwort
 * tatsaechlich mitliefert (`active` steht in beiden Serverfassungen im Koerper - `server.js`
 * `publicUser()` und `backend/src/http/routes/auth.ts`).
 */
export interface AngemeldeterNutzer {
  user: Nutzer;
  permissions: readonly string[];
  token: string;
  /** KI-Zugang eingeschaltet? Gleiches Feld wie in `GET /api/auth/me` (`api/auth.ts#ProfilErgebnis`); fehlt bei einem aelteren Backend. */
  kiZugangAktiv?: boolean;
}

export interface AnmeldungProps {
  onAngemeldet: (ergebnis: AngemeldeterNutzer) => void;
  /**
   * Ein Hinweis, der beim Start in der Fehlerzeile der Maske steht (Fehleranzeige Paket 2): die Pruefung der
   * gespeicherten Anmeldung ist gescheitert, ohne dass die Sitzung abgelaufen waere (`App.tsx`). Er steht dort, bis der
   * Nutzer eine Anmeldung abschickt; danach zeigt die Zeile nur noch das Ergebnis dieser Anmeldung. `null` und
   * weggelassen heissen: kein Hinweis.
   */
  startHinweis?: string | null;
}

/** Eine Antwort mit Erfolgsstatus, der `token` oder `user` fehlt (Fehleranzeige Paket 2; ohne Text in `FEHLERTEXTE`, weil nur diese Maske ihn braucht). */
const ANMELDEANTWORT_UNGUELTIG =
  "Der Server hat keine gültige Anmeldeantwort geliefert. Bitte erneut versuchen; tritt es wieder auf, bitte Bescheid geben.";

/**
 * Der Antwortkoerper als JSON. Alles andere - Proxy-HTML, ein leerer oder abgebrochener Koerper - ist
 * fuer die Maske "kein Koerper": bei einem Fehlstatus sagt dann allein der Status etwas (`fehlertext`),
 * bei einem Erfolgsstatus ist die Antwort unbrauchbar (siehe `anmeldeAnfrage`).
 */
async function jsonOderNichts(antwort: Response): Promise<unknown> {
  try {
    return await antwort.json();
  } catch { // still: kein JSON heisst kein Koerper; der Aufrufer meldet das ueber Status bzw. Satz
    return undefined;
  }
}

/**
 * `POST /api/auth/login` mit dem EIGENEN `fetch` der Maske (nicht `anfrage()`: dessen 401-Behandlung
 * wuerde den Sitzungsverlust ausloesen, und hier heisst der 401 schlicht "falsches Passwort"). Wirft
 * statt Text zu liefern, damit `fehlertext()` den Wortlaut bestimmt:
 * - `fetch` selbst scheitert (kein Netz): `VerbindungsFehler`.
 * - Fehlstatus: `ApiFehler(status, …, koerper)` mit dem gelesenen Koerper - NIE ein `SitzungsFehler`,
 *   denn `fehlertext(SitzungsFehler)` ergaebe `null` und der Nutzer saehe keinen Grund.
 * - Erfolgsstatus ohne JSON-Objekt (z. B. die Startseite statt der API): ein `NutzerFehler` mit
 *   `FEHLERTEXTE.serverAntwortetNicht`.
 * - Erfolgsstatus mit JSON-Objekt, dem `token` (String) oder `user` (Objekt) fehlt (z. B. `{}`): ein
 *   `NutzerFehler` mit `ANMELDEANTWORT_UNGUELTIG`. Vorher reichte die Maske den halben Koerper weiter, und
 *   `setzeSitzungsToken(undefined)` loeschte das Token, ohne dass der Nutzer etwas erfuhr.
 */
async function anmeldeAnfrage(username: string, passwort: string): Promise<AngemeldeterNutzer> {
  let antwort: Response;
  try {
    antwort = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ username, password: passwort }),
    });
  } catch (ursache) {
    throw new VerbindungsFehler(ursache);
  }
  const koerper = await jsonOderNichts(antwort);
  if (!antwort.ok) throw new ApiFehler(antwort.status, `HTTP ${antwort.status}`, koerper);
  if (typeof koerper !== "object" || koerper === null) {
    throw new NutzerFehler(FEHLERTEXTE.serverAntwortetNicht);
  }
  const { token, user } = koerper as { token?: unknown; user?: unknown };
  if (typeof token !== "string" || token === "" || typeof user !== "object" || user === null) {
    throw new NutzerFehler(ANMELDEANTWORT_UNGUELTIG);
  }
  return koerper as AngemeldeterNutzer;
}

/**
 * `#login`/`.login-box` (index.html:3995-4006) samt `doLogin()` (index.html:5694-5724). Task 8
 * baut den eigentlichen API-Client (`src/api/`) - hier ein bewusst kleiner, direkter `fetch`-Aufruf
 * auf denselben Endpunkt, ausschließlich damit sich die App überhaupt anmelden kann (der
 * Kopfzeilen-Bildvergleich, Task-Brief Schritt 6, meldet sich über `harness.ts#anmelden` mit
 * echten Zugangsdaten an einem echten lokalen Server an).
 *
 * `.login-box`-Kinder wörtlich in der Reihenfolge des Originals (kein zusätzliches `<form>`):
 * Logo, Überschrift, Text, beide Eingaben, Hinweis, Knopf, Fehlermeldung. Statt eines
 * `onsubmit`-Handlers deshalb wie im Original ein `onKeyDown` am Passwortfeld
 * (`if(event.key==='Enter')doLogin()`, index.html:4001) und `onClick` am Knopf.
 *
 * `id="login"` bekommt die Klasse `show` fest (nicht bedingt wie im Original): die SPA rendert
 * `<Anmeldung>` ohnehin nur, solange keine Sitzung besteht (siehe `App.tsx`) - das Umschalten der
 * Sichtbarkeit übernimmt React-Mounting/Unmounting statt einer CSS-Klasse. Ohne `show` bliebe das
 * Formular unsichtbar: das unveränderte, wörtlich übernommene Stylesheet (`bestand.css`) setzt
 * `#login { display: none }` und erst `#login.show { display: flex }`.
 */
export function Anmeldung({ onAngemeldet, startHinweis = null }: AnmeldungProps): JSX.Element {
  const [benutzername, setBenutzername] = useState("");
  const [passwort, setPasswort] = useState("");
  const [fehler, setFehler] = useState("");
  const [sendetGerade, setSendetGerade] = useState(false);
  // Der Startsatz (`startHinweis`) steht in derselben Fehlerzeile, bis der Nutzer zum ersten Mal abschickt - auch
  // wenn die Pruefung leerer Felder dann greift. Danach zeigt die Zeile nur noch `fehler`.
  const [abgeschickt, setAbgeschickt] = useState(false);
  const fehlerzeile = abgeschickt ? fehler : (startHinweis ?? "");

  async function anmelden(): Promise<void> {
    setAbgeschickt(true);
    const username = benutzername.trim();
    if (!username || !passwort) {
      setFehler("Benutzername und Passwort eingeben");
      return;
    }
    setSendetGerade(true);
    try {
      const ergebnis = await anmeldeAnfrage(username, passwort);
      setPasswort("");
      setFehler("");
      onAngemeldet(ergebnis);
    } catch (ursache) {
      // Die Fehlerzeile der Maske gab es schon (Spec R2, "Inline bleibt inline"): neu ist nur der Text.
      // Ohne `kontext`, damit etwa der Servertext "Benutzername oder Passwort falsch." unveraendert
      // dasteht. `null` (Abbruch, 401) kommt hier nie vor: die Anfrage hat kein `signal`, und ein 401
      // ist ein ApiFehler (siehe `anmeldeAnfrage`).
      setFehler(fehlertext(ursache) ?? "");
    } finally {
      setSendetGerade(false);
    }
  }

  function beiEnterZuPasswort(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === "Enter") document.getElementById("loginPassword")?.focus();
  }

  function beiEnterAnmelden(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === "Enter") void anmelden();
  }

  return (
    <div id="login" className="show">
      <div className="login-box">
        <img src="ABSINA_Logo_Schwarz_1500x287px_300dpi.png" alt="ABSINA" />
        <h2>Bestell-Tracking Hub</h2>
        <p>Mit deinem persönlichen Konto anmelden</p>
        <input
          type="text"
          id="loginUser"
          placeholder="Benutzername"
          autoComplete="username"
          value={benutzername}
          onChange={(event) => setBenutzername(event.target.value)}
          onKeyDown={beiEnterZuPasswort}
        />
        <input
          type="password"
          id="loginPassword"
          placeholder="Passwort"
          autoComplete="current-password"
          value={passwort}
          onChange={(event) => setPasswort(event.target.value)}
          onKeyDown={beiEnterAnmelden}
        />
        <div className="login-hint">
          Erster Login: <b>admin</b> + bisheriger Zugangscode
        </div>
        <button type="button" disabled={sendetGerade} onClick={() => void anmelden()}>
          <LogIn aria-hidden="true" strokeWidth={1.8} />
          Anmelden
        </button>
        <div className="login-err" id="loginErr">
          {fehlerzeile}
        </div>
      </div>
    </div>
  );
}
